import { StripeAccountStatus } from '../../hooks/useRent'
import { IdentifiableObject } from '../types'
import { SimpleUser } from '../Users'

export enum PaymentStatus {
    PENDING = 'pending',
    SUCCEEDED = 'succeeded',
    FAILED = 'failed',
    CANCELED = 'canceled',
    REFUNDED = 'refunded',
}

export const PaymentStatusLabels: Record<PaymentStatus, string> = {
    [PaymentStatus.PENDING]: 'Pending',
    [PaymentStatus.SUCCEEDED]: 'Succeeded',
    [PaymentStatus.FAILED]: 'Failed',
    [PaymentStatus.CANCELED]: 'Canceled',
    [PaymentStatus.REFUNDED]: 'Refunded',
}

export enum PaymentMethod {
    STRIPE = 'stripe',
    CASH = 'cash',
    CHECK = 'check',
    BANK_TRANSFER = 'bank_transfer',
    OTHER = 'other',
}

export const PaymentMethodLabels: Record<PaymentMethod, string> = {
    [PaymentMethod.STRIPE]: 'Stripe',
    [PaymentMethod.CASH]: 'Cash',
    [PaymentMethod.CHECK]: 'Check',
    [PaymentMethod.BANK_TRANSFER]: 'Bank Transfer',
    [PaymentMethod.OTHER]: 'Other',
}

export enum BillStatus {
    PENDING = 'pending',
    DRAFT = 'draft',
    SENT = 'sent',
    PAID = 'paid',
    OVERDUE = 'overdue',
    VOIDED = 'voided',
    PARTIAL = 'partial',
}

export const BillStatusLabels: Record<BillStatus, string> = {
    [BillStatus.DRAFT]: 'Draft',
    [BillStatus.SENT]: 'Sent',
    [BillStatus.PAID]: 'Paid',
    [BillStatus.OVERDUE]: 'Overdue',
    [BillStatus.VOIDED]: 'Voided',
    [BillStatus.PARTIAL]: 'Partially Paid',
    [BillStatus.PENDING]: 'Pending',
}

export interface TenantPayment extends IdentifiableObject {
    payment_id: string
    bill: number
    amount: number
    status: PaymentStatus
    payment_method: PaymentMethod
    stripe_payment_intent_id: string | null
    stripe_transfer_id: string | null
    payment_date: string
    notes: string | null
    created_date: string
    modified_date: string
    is_voided: boolean
    voided_by: SimpleUser | null
    voided_at: string | null
    void_reason: string | null
}

export interface TenantBillItem extends IdentifiableObject {
    bill: number
    scheduled_charge: number
    description: string
    amount: number
    charge_code: string
    frequency: string
    period_start: string
    period_end: string
    created_date: string
    modified_date: string
    is_voided: boolean
    voided_by: SimpleUser | null
    voided_at: string | null
    void_reason: string | null
}

export interface TenantLease extends IdentifiableObject {
    start_date: string
    end_date: string
    rent: number
    area: {
        id: number
        area_label: string
        area_config: {
            id: number
            apartment: number
            name: string
            occupiable: boolean
            shows_on_schedule: boolean
            area_type: string
        }
        unit: {
            id: number
            name: string
            folder: number
        }
    }
}

export interface TenantBill extends IdentifiableObject {
    bill_id: string
    tenant: SimpleUser
    lease: TenantLease
    apartment: number
    billing_month: string
    due_date: string
    subtotal: number
    tax_amount: number
    late_fee: number
    total_amount: number
    amount_paid: number
    status: BillStatus
    created_at: string
    sent_at: string | null
    paid_at: string | null
    created_date: string
    modified_date: string
    bill_items: TenantBillItem[]
    payments: TenantPayment[]
    balance_due: number
    is_overdue: boolean
    is_paid: boolean
    stripe_invoice_id: string | null
    stripe_payment_intent_id: string | null
    notes: string | null
    is_voided: boolean
    voided_by: SimpleUser | null
    voided_at: string | null
    void_reason: string | null
}

export interface DelinquentAccount extends TenantLease {
    tenant: SimpleUser
    total_amount_owed: number
    days_behind: number
    last_payment_date: string | null
    last_payment_amount: number | null
    total_overdue_amount: number
}

export interface StripeConnectAccount extends IdentifiableObject {
    stripe_account_id: string
    account_type: string
    status: StripeAccountStatus
    charges_enabled: boolean
    payouts_enabled: boolean
    details_submitted: boolean
    created_date: string
    modified_date: string
    ezos_application_flat_fee: number
    ezos_application_percentage_fee: number
}
